import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import type React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface ModalProps {
	id: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string | null;
	description?: string | null;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
	children?: ReactNode | null;
	backdrop?: boolean | 'static';
	keyboard?: boolean;
	closeButton?: boolean;
}

const sizeMap = {
	xs: 'sm:max-w-md',
	sm: 'sm:max-w-lg',
	md: 'sm:max-w-xl',
	lg: 'sm:max-w-2xl',
	xl: 'sm:max-w-3xl'
};

const Modal = ({
	id,
	open,
	onOpenChange,
	title = null,
	description = null,
	size = 'lg',
	children = null,
	backdrop = true,
	keyboard = true,
	closeButton = false
}: ModalProps): React.JSX.Element => {
	const { t } = useTranslation();

	const handleInteractOutside = (e: Event) => {
		// Handle backdrop options
		if (backdrop === false || backdrop === 'static') {
			e.preventDefault();
		}
	};

	const handleEscapeKeyDown = (e: KeyboardEvent) => {
		// Handle keyboard option
		if (!keyboard) {
			e.preventDefault();
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={cn(sizeMap[size], 'p-3 md:p-5 flex flex-col')}
				data-testid={`modal-${id}`}
				showCloseButton={closeButton}
				onInteractOutside={handleInteractOutside}
				onEscapeKeyDown={handleEscapeKeyDown}
			>
				{title ? (
					<DialogHeader className={cn('shrink-0', 'px-6 pt-6 pb-0')}>
						<DialogTitle>{title}</DialogTitle>
						{description && <DialogDescription>{description}</DialogDescription>}
					</DialogHeader>
				) : (
					<VisuallyHidden.Root>
						<DialogHeader>
							<DialogTitle>{t('modals.dialog.title')}</DialogTitle>
							{description ? (
								<DialogDescription>{description}</DialogDescription>
							) : (
								<DialogDescription>{t('modals.dialog.content')}</DialogDescription>
							)}
						</DialogHeader>
					</VisuallyHidden.Root>
				)}
				<div className="overflow-y-auto flex-1 min-h-0">{children}</div>
			</DialogContent>
		</Dialog>
	);
};

export default Modal;
